import { Component, Event, EventEmitter, h, Prop, State, Watch } from '@stencil/core';

@Component({
  tag: 'pnk-select',
  styleUrl: 'pnk-select.css',
})
export class PnkSelect {
  @Prop() label: string = '';

  @Prop() placeholder: string = '';

  @Prop({ mutable: true, reflect: true }) value: string | null = null;

  @Prop({ mutable: true }) error: string | null = null;

  @Prop({ mutable: true }) maxOptionsDisplayed: number = Infinity;

  @Prop({ mutable: true }) loading: boolean = true;

  @Prop({ mutable: true }) disabled: boolean = false;

  @Prop({ mutable: true }) optionsData: {
    value: string;
    text: string;
  }[] = [];

  @State() isHovered: boolean = false;

  @Event() pnkChange!: EventEmitter<{
    value: string;
    text: string;
  }>;

  textInput?: HTMLInputElement;

  optionContainer?: HTMLDivElement;

  // needed: chrome will popup account password support selection otherwise
  private randomName: string = `${Math.floor(Math.random() * 1000)}`;

  private selected: {
    value: string;
    text: string;
  } | undefined;

  // propagate value change from model to view
  @Watch('value')
  valueChanged(newValue: string, oldValue: string) {
    if (newValue !== oldValue) {
      const initialValue = this.optionsData.find(x => x.value === newValue);
      if (initialValue && initialValue !== undefined) {
        this.selectOption(initialValue);
      }
      this.pnkChange.emit(this.selected);
      this.updateTextInput();
    }
  }

  @Watch('optionsData')
  optionsDataChanged(newValue: string, oldValue: string) {
    if (newValue !== oldValue) {
      this.forceUpdateSelected();
    }
  }

  @Watch('loading')
  loadingChanged(newValue: boolean, oldValue: boolean) {
    if (newValue !== oldValue) {
      this.disabled = Boolean(newValue);
    }
  }

  componentDidLoad() {
    // this.setupOptions();
    this.forceUpdateSelected();
  }

  // propagate value change from view to model
  selectOption(selected: {
    value: string;
    text: string;
  }) {
    if (selected?.value) {
      this.selected = selected;
      this.value = selected.value;
      this.hideOptions();
    }
  }

  forceUpdateSelected() {
    this.selected = this.optionsData.find(optionDatum => optionDatum.value === this.value);
    this.updateTextInput();
  }

  updateTextInput() {
    if (this.textInput && this.selected) {
      this.textInput.value = this.selected.text;
    }
  }

  toggleOptions() {
    if (this.optionContainer?.hidden) {
      this.showOptions();
    } else {
      this.hideOptions();
    }
  }

  showOptions() {
    if (!this.loading && !this.disabled && this.optionContainer) {
      this.optionContainer.focus();
      this.optionContainer.hidden = false;
    }
  }

  hideOptions() {
    setTimeout(() => {
      if (this.optionContainer) {
        this.optionContainer.hidden = true;
      }
    }, 100);
  }

  render() {
    const style = {
      'pnk-border pnk-bg-disabled-base pnk-w-inherit pnk-rounded-md focus:pnk-outline-none': true,
      'pnk-bg-disabled-base pnk-text-black': !this.disabled && !this.error,
      'pnk-bg-red pnk-text-white': Boolean(this.error),
      'pnk-bg-disabled-text pnk-text-white pnk-border-base-300 pnk-text-base-300 pnk-text-base-content-300': this.disabled && !this.error,
      'cursor-default': true,
    };

    const inputStyle = {
      'relative': true,
      'pnk-h-full': true,
      'arrow': !this.disabled,
      'arrow2': this.disabled,
      ...style,
    };

    const optionsContainerStyle = {
      'absolute': true,
      'pnk-h-auto': true,
      'options-container': true,
      'pointer': true,
      'pnk-bg-option': true,
      ...style,
    };

    return (
      <div class="input-container pnk-flex pnk-flex-col relative pnk-w-full pnk-h-full pnk-min-w-[3rem]">
        <label class="pnk-text-disabled-text pnk-font-bold pnk-text-[13px] pnk-ml-2 pnk-mb-[1px]">{this.label}</label>

        <input
          class={inputStyle}
          type="text"
          onMouseEnter={() => !this.disabled && this.textInput?.style.setProperty('background-image', `url(${'./assets/red-arrow.svg'})`)}
          onMouseLeave={() => this.textInput?.style.removeProperty('background-image')}
          name={this.randomName}
          onBlur={() => this.hideOptions()}
          onClick={() => this.toggleOptions()}
          ref={el => (this.textInput = el as HTMLInputElement)}
          value={this.selected?.text}
          placeholder={this.placeholder}
          readonly
          disabled={this.disabled}
        />
        <div class={optionsContainerStyle} hidden ref={el => (this.optionContainer = el as HTMLDivElement)}>
          {this.optionsData?.map(option => (
            <option class="search-option" value={option.value} onClick={() => this.selectOption(option)}>
              {option?.text ? option.text : ''}
            </option>
          ))}
        </div>
      </div>
    );
  }
}
